<template>
    <div class="container mx-auto px-0 py-2">
        <div class="flex justify-between items-center mb-6">
            <div>
                <h1 class="text-3xl font-bold text-gray-800 dark:text-white tracking-tight">Tags</h1>
                <p class="mt-2 text-gray-600 dark:text-gray-400">Manage and organize your tags</p>
            </div>
            <button 
                @click="createTag" 
                class="btn rounded-full text-white bg-echoes-primary hover:bg-echoes-primary/80 border-transparent"
            >
                <i class="mdi mdi-plus"></i> New Tag
            </button>
        </div>

        <!-- Search and Filter Section -->
        <div class="mb-6 space-y-4">
            <!-- Search Bar -->
            <div class="relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i class="mdi mdi-magnify text-gray-400"></i>
                </div>
                <input
                    type="text"
                    v-model="searchQuery"
                    placeholder="Search tags..."
                    class="block w-full pl-10 pr-3 py-2 border border-echoes-border-gray rounded-lg
                           bg-echoes-background-black text-echoes-text-white
                           placeholder-echoes-text-gray-500
                           focus:outline-none focus:border-echoes-primary"
                />
            </div>

        </div>

        <!-- Modal de Criação/Edição -->
        <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto ">
            <div class="flex items-center justify-center min-h-screen px-0">
                <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity" @click="closeModal"></div>
                
                <div class="relative bg-echoes-background-black rounded-[21px] shadow-xl max-w-md w-full border border-echoes-border-gray">
                    <div class="flex justify-between items-center px-6 py-4 border-b border-echoes-border-gray">
                        <h2 class="text-xl font-semibold text-white">
                            {{ isEditing ? 'Edit Tag' : 'New Tag' }}
                        </h2>
                        <button @click="closeModal" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ms-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white">
                            <svg class="size-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                            </svg>
                        </button>
                    </div>

                    <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
                        <div class="flex gap-2 items-center">
                            <div class="flex flex-col gap-1 w-full">
                            <label for="tagName" class="block text-sm font-medium text-white mb-1">
                                Tag Name
                            </label>
                            <TextInput v-model="newTag" placeholder="Enter tag name" />

                            </div>

                            <div class="flex flex-col gap-1">
                            <label for="tagIcon" class="block text-sm font-medium text-white mb-1">
                                Tag Icon
                            </label>
                            <IconSelect v-model="selectedIcon" placeholder="Select icon" />
                            </div>
                        </div>

                        <div>
                            <div class="flex gap-2 items-center mb-1">
                                <label for="tagType" class="block text-sm font-medium text-white">
                                    Tag Type
                                </label>
                                <button 
                                    type="button"
                                    @click="isCustomType = !isCustomType"
                                    class="text-echoes-primary hover:text-echoes-primary dark:text-echoes-primary dark:hover:text-echoes-primary"
                                    :title="isCustomType ? 'Switch to predefined types' : 'Add custom type'"
                                >
                                    <i class="mdi" :class="isCustomType ? 'mdi-format-list-bulleted' : 'mdi-plus'"></i>
                                </button>
                            </div>
                            <div v-if="isCustomType">
                                <TextInput id="tagType" v-model="newTagType" placeholder="Enter custom tag type" />
                            </div>
                            <div v-else>
                                <SelectInput id="tagType" :options="tagTypes" v-model="newTagType" placeholder="Select tag type" />
                            </div>
                        </div>

                        <div>
                            
                        </div>

                        <div>
                            <label for="tagColor" class="block text-sm font-medium text-white mb-1">
                                Tag Color
                            </label>
                            <div class="flex items-center gap-4">
                                <input
                                    type="color"
                                    id="tagColor"
                                    v-model="selectedColor"
                                    class="h-10 w-20 rounded-lg cursor-pointer border-none bg-transparent "
                                >
                                <div class="flex w-[96px]">
                                    <TextInput :autocomplete="'off'" id="tagColor" v-model="selectedColor" />
                                </div>
                            </div>
                        </div>

                        <div class="mt-4">
                            <label for="tagPreview" class="block text-sm font-medium text-white mb-1">
                                Preview
                            </label>
                            <div id="tagPreview" class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
                                :style="{ backgroundColor: selectedColor, color: getContrastColor(selectedColor) }">
                                <i v-if="selectedIcon" :class="[getIconClass(selectedIcon), 'text-sm']"></i>
                                {{ newTag || 'Tag Preview' }}
                            </div>
                            <span class="ml-2 text-sm text-white/50">
                                ({{ newTagType }})
                            </span>
                        </div>

                        <div class="flex justify-end gap-3 mt-6">
                            <button 
                                type="button"
                                @click="closeModal"
                                class="btn rounded-full bg-echoes-background-gray text-echoes-text-gray hover:bg-echoes-background-gray/80 border-transparent px-4 py-1.5 text-sm"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                class="btn rounded-full text-white bg-echoes-primary hover:bg-echoes-primary/80 border-transparent px-4 py-1.5 text-sm"
                            >
                                {{ isEditing ? 'Save Changes' : 'Create Tag' }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Tabela de Tags -->
        <div class="bg-echoes-background-black rounded-lg shadow overflow-hidden w-full">
            <div class="max-h-[600px] overflow-y-auto w-full">
                <BaseTable :columnDefs="columns" :rowData="rowData" :quickFilterText="searchQuery" :useFitGridWidth="true" :rowHeight="50" :tableHeight="600" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import BaseTable from '@/components/table/BaseTable.vue';
import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast';
import IconSelect from '@/components/forms/inputs/IconSelect.vue';
import { useIconSelect } from '@/composables/useIconSelect.js';

const tagTypes = ref([]);
const showModal = ref(false);
const newTag = ref('');
const selectedColor = ref('#3B82F6');
const newTagType = ref('');
const selectedFilterType = ref('all');
const searchQuery = ref('');
const isEditing = ref(false);
const editingTagId = ref(null);
const isCustomType = ref(false);
const selectedIcon = ref('');

const getIconClass = (icon) => {
    if (!icon) return '';
    
    if (icon.includes(' ')) {
        return icon;
    }
    
    const iconObj = icons.value.find(i => i.name === icon);
    return iconObj ? iconObj.class : '';
};
const rowData = ref([]);

import { useLayoutComponents } from '@/composables/layout.js';

const { addToast } = useToast();

const { SelectInput, TextInput } = useLayoutComponents();
const { getAllIcons } = useIconSelect();
const icons = ref(getAllIcons());

onMounted(async () => {
    await fetchTags();
    await fetchTagTypes();
});

const getBadgeConfig = (params) => {
    return {
        innerHTML: `<div class="inline-block px-3 py-1 rounded-full text-sm font-medium"
            style="background-color: ${params.data.display_color}; color: ${getContrastColor(params.data.display_color)}">
            ${params.data.name}
        </div>`,
    }
}

const columns = [
    {
        headerName: 'Tag',
        field: 'name',
        type: 'tag',
        flex: 10,
        minWidth: 400,
        maxWidth: 1000,
        suppressSizeToFit: false,
        cellStyle: {
            paddingRight: 0,
        },
        cellRenderer: getBadgeConfig,
    },
    {
        headerName: 'Type',
        field: 'type',
        flex: 5,
        minWidth: 250,
        maxWidth: 500,
        suppressSizeToFit: false,
        type: 'text',
    },
    {
        headerName: 'Actions',
        field: 'actions',
        flex: 3,
        minWidth: 250,
        maxWidth: 300,
        suppressSizeToFit: false,
        cellRenderer: (params) => {
            const container = document.createElement('div');
            container.className = 'flex items-center gap-2';
            
            // Edit Button
            const editBtn = document.createElement('button');
            editBtn.className = 'flex items-center gap-2 px-2.5 text-gray-100 rounded-md h-7 bg-violet-500 hover:bg-violet-600';
            editBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg> Edit`;
            editBtn.onclick = () => editTag(params.data);
            
            // Delete Button
            const deleteBtn = document.createElement('button');
            deleteBtn.className = 'flex items-center gap-2 px-2.5 text-gray-100 rounded-md h-7 bg-red-500 hover:bg-red-600';
            deleteBtn.innerHTML = `
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>Delete`;
            deleteBtn.onclick = () => deleteTag(params.data);
            
            container.appendChild(editBtn);
            container.appendChild(deleteBtn);
            return container;
        },
    }
];

const fetchTags = async () => {
    try {
        const response = await axios.get('/api/tags');
        if(response.status === 200) {
            selectedFilterType.value = 'all';
            rowData.value = response.data.map(tag => ({
                id: tag.id,
                name: tag.name,
                display_color: tag.display_color,
                display_icon: tag.display_icon,
                type: tag?.tag_type?.type || '-',
                type_tooltip: tag?.tag_type,
                tag_type_id: tag?.tag_type_id,
                original_type: tag?.tag_type?.type
            }));
        } else {
            console.error('Error fetching tags:', response.data);
        }
    } catch (error) {
        console.error('Error fetching tags:', error);
    }
};

const fetchTagTypes = async () => {
    try {
        const response = await axios.get('/api/tag_types');
        if(response.status === 200) {
            tagTypes.value = response.data.map(tagTypes => ({
                label: tagTypes.type,
                value: tagTypes.type
            }));
        } else {
            console.error('Error fetching tags:', response.data);
        }
    } catch (error) {
        console.error('Error fetching tags:', error);
    }
};

const getContrastColor = (hexColor) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return yiq >= 128 ? '#000000' : '#FFFFFF';
};

const createTag = () => {
    isEditing.value = false;
    newTag.value = '';
    selectedColor.value = '#3B82F6';
    newTagType.value = '';
    selectedIcon.value = '';
    showModal.value = true;
};

const editTag = (tag) => {
    isEditing.value = true;
    editingTagId.value = tag.id;
    newTag.value = tag.name;
    selectedColor.value = tag.display_color;
    newTagType.value = tag.original_type;
    selectedIcon.value = tag.display_icon || '';
    showModal.value = true;
};



const closeModal = () => {
    showModal.value = false;
    newTag.value = '';
    selectedColor.value = '#3B82F6';
    newTagType.value = '';
    selectedIcon.value = '';
    isEditing.value = false;
    editingTagId.value = null;
    isCustomType.value = false;
};

const deleteTag = async (tag) => {
    try {
        if(confirm('Are you sure you want to delete this tag?') && tag?.id) {
            const response = await axios.post(`/api/tags/${tag.id}/delete`);
            if(response.status === 200 || response.status === 201) {
                await fetchTags();
                addToast('Tag Deleted','success');
            } else {
                addToast(response.data.message || 'An error occurred','error');
            }
        }
    } catch (error) {
        addToast(error.response?.data?.message || 'An error occurred','error');
        console.error('Error deleting tag:', error);
    }
};

const handleSubmit = async () => {
    const url = isEditing.value ? `/api/tags/${editingTagId.value}` : '/api/tags';
    const payload = {
        name: newTag.value,
        display_color: selectedColor.value,
        display_icon: selectedIcon.value,
        type: newTagType.value,
    };

    try {
        const response = await axios.post(url, payload);
        if (response.status === 201) {
            await fetchTags();
            closeModal();
        } else {
            console.error('Error creating/updating tag:', response.data);
        }
    } catch (error) {
        console.error('Error creating/updating tag:', error);
    }
    closeModal();
};
</script>